import { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LogoMark } from '@liminal/web-ui/brand/generated/LogoMark';
import { brandColors } from '@liminal/brand/palette';
import { borderRadius, fontSizes, fontWeights, letterSpacing, spacing } from '@liminal/brand/scale';
import { withOpacity } from '@liminal/brand/color-utils';
import { FONT_STACK_DISPLAY } from '@liminal/brand/font-faces';

/**
 * A phone notification banner, as the push lands on a locked screen.
 *
 * Deliberately generic-OS rather than a pixel copy of iOS or Android: the two
 * differ, and the thing worth checking before sending to everyone is whether
 * the title fits on one line and where the message truncates. Both platforms
 * clip the body at roughly this width, so that check holds.
 */

const Wallpaper = styled(Box)({
  background: `linear-gradient(160deg, ${brandColors.brown}, ${brandColors.black})`,
  borderRadius: borderRadius['2xl'],
  padding: spacing.md,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,
  minHeight: 200,
  justifyContent: 'center',
});

const Banner = styled(Box)({
  backgroundColor: withOpacity(brandColors.white, 0.92),
  borderRadius: borderRadius.lg,
  padding: spacing.sm + 4,
  display: 'flex',
  gap: spacing.sm,
  alignItems: 'flex-start',
});

const AppIcon = styled(Box)({
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: borderRadius.md,
  backgroundColor: brandColors.cream,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const MetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing.sm,
  marginBottom: 2,
});

const AppName = styled(Typography)({
  fontFamily: FONT_STACK_DISPLAY,
  fontSize: fontSizes.xs - 1,
  fontWeight: fontWeights.semibold,
  letterSpacing: `${letterSpacing.wide}px`,
  textTransform: 'uppercase',
  color: brandColors.grey,
});

const NotificationTitle = styled(Typography)({
  fontFamily: FONT_STACK_DISPLAY,
  fontSize: fontSizes.sm,
  fontWeight: fontWeights.semibold,
  color: brandColors.black,
  overflowWrap: 'anywhere',
});

const NotificationMessage = styled(Typography)({
  fontFamily: FONT_STACK_DISPLAY,
  fontSize: fontSizes.xs + 1,
  lineHeight: 1.45,
  color: brandColors.black,
  overflowWrap: 'anywhere',
  // Both platforms collapse a long body to a few lines until the user expands
  // the notification. Showing it untruncated would hide the copy problem the
  // preview exists to reveal.
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 4,
  overflow: 'hidden',
});

export interface PushAnnouncementPreviewProps {
  readonly title: string;
  readonly message: string;
  /** Internal app path opened on tap, e.g. `/savings`. */
  readonly url: string;
  /** When the push is scheduled for, or null for an immediate send. */
  readonly scheduledFor: Date | null;
}

export const PushAnnouncementPreview = memo(function PushAnnouncementPreview({
  title,
  message,
  url,
  scheduledFor,
}: PushAnnouncementPreviewProps) {
  // A notification stamps a short time, not a full date — and the banner is
  // only so wide. `formatDateTime`'s long form overflows it.
  const timeLabel = scheduledFor
    ? scheduledFor.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'now';
  const trimmedTitle = title.trim();
  const trimmedMessage = message.trim();
  const trimmedUrl = url.trim();

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Preview
      </Typography>
      <Wallpaper>
        <Banner>
          <AppIcon>
            <LogoMark size={18} />
          </AppIcon>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <MetaRow>
              <AppName sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Liminal
              </AppName>
              <AppName
                sx={{
                  letterSpacing: `${letterSpacing.normal}px`,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {timeLabel}
              </AppName>
            </MetaRow>
            {/* Placeholders are greyed so an empty field never reads as copy
                that is about to ship. */}
            <NotificationTitle sx={trimmedTitle ? undefined : { color: brandColors.greyLighter }}>
              {trimmedTitle || 'Notification title'}
            </NotificationTitle>
            <NotificationMessage
              sx={trimmedMessage ? undefined : { color: brandColors.greyLighter }}
            >
              {trimmedMessage || 'The notification message appears here.'}
            </NotificationMessage>
          </Box>
        </Banner>
      </Wallpaper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {trimmedUrl
          ? `Tapping it opens ${trimmedUrl} in the app.`
          : 'No deep link — tapping it just opens the app.'}
      </Typography>
    </Box>
  );
});
